import { useEffect, useId, useRef } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description } from '@headlessui/react';
import { X } from 'lucide-react';
import clsx from 'clsx';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

/**
 * Open modals, newest last. Escape only ever closes the top one, so a confirm
 * dialog opened from inside a modal never takes its parent down with it.
 */
const openStack: string[] = [];

function useEscapeToClose(id: string, isOpen: boolean, onClose: () => void) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    openStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || openStack[openStack.length - 1] !== id) return;
      e.preventDefault();
      onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const idx = openStack.lastIndexOf(id);
      if (idx !== -1) openStack.splice(idx, 1);
    };
  }, [id, isOpen]);
}

export function Modal({ isOpen, onClose, title, description, children, size = 'md' }: ModalProps) {
  const id = useId();
  useEscapeToClose(id, isOpen, onClose);

  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-4xl',
  };

  return (
    // Headless UI closes on a click outside the panel (the backdrop) and on Escape;
    // the explicit listener above covers focus that starts outside the panel.
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-surface-inverse/50 transition-opacity duration-100 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className={clsx(
              'w-full rounded-2xl bg-surface-primary shadow-strong transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0',
              sizes[size]
            )}
          >
            {(title || description) && (
              <div className="flex items-start justify-between gap-4 border-b border-border-light px-6 py-4">
                <div>
                  {title && (
                    <DialogTitle as="h3" className="text-heading-md text-text-primary">
                      {title}
                    </DialogTitle>
                  )}
                  {description && (
                    <Description className="mt-1 text-body-sm text-text-tertiary">{description}</Description>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded-full p-1.5 text-text-muted hover:bg-surface-secondary hover:text-text-secondary transition-colors"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="px-6 py-4">{children}</div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

export function ModalFooter({ children, className }: ModalFooterProps) {
  return (
    <div
      className={clsx(
        'flex items-center justify-end gap-3 border-t border-border-light px-6 py-4 -mx-6 -mb-4 mt-4',
        className
      )}
    >
      {children}
    </div>
  );
}
